class OffCanvas {
  constructor(canvasElement) {
    this.canvasElement = canvasElement;

    // Initialize DOM elements
    this.openButton = document.querySelector(`[data-canvas-target="${canvasElement.dataset.canvas}"]`);
    this.closeButton = this.canvasElement.querySelector('.canvas-close');
    this.content = this.canvasElement.querySelector('.canvas-content');
    this.nestedMenus = this.canvasElement.querySelectorAll('.canvas-nested');
    this.nestedContents = this.canvasElement.querySelectorAll('.canvas-nested-content');
    this.backButtons = this.canvasElement.querySelectorAll('.canvas-nested-back');
    this.overlayElement = document.querySelector('.canvas-overlay');

    // State variables
    this.isOpen = false;
    this.activeMenu = null;

    this.init();
  }

  // Initializes the OffCanvas component by setting up event listeners
  init() {
    this.addEventListeners();
    this.setInitialFocusState();
  }

  // Adds all event listeners to the component
  addEventListeners() {
    this.openButton.addEventListener('click', () => this.open());
    this.closeButton.addEventListener('click', () => this.close());
    this.overlayElement.addEventListener('click', () => this.close());

    this.nestedMenus.forEach((menu) =>
      menu.addEventListener('click', (event) => this.handleNestedMenuClick(event, menu))
    );

    this.backButtons.forEach((backButton) =>
      backButton.addEventListener('click', (event) => this.handleBackButtonClick(event))
    );

    document.addEventListener('keydown', (event) => this.handleKeyDown(event));
  }

  // Disables focus for nested contents initially
  setInitialFocusState() {
    this.nestedContents.forEach((nestedContent) => this.toggleFocus(nestedContent, false));
  }

  // Toggles focusable state of elements within a given element
  toggleFocus(element, enable) {
    const focusableElements = element.querySelectorAll('a, button, input, textarea');
    focusableElements.forEach((el) => (el.inert = !enable));
  }

  // Opens the off-canvas menu
  open() {
    this.canvasElement.classList.add('active');
    this.isOpen = true;

    this.updateAccessibilityAttributes(true);

    // Disable page scroll
    document.body.classList.add('noscroll');

    // Focus on first element
    this.updateFocusableElements(this.canvasElement);
    (this.firstFocusableElement || this.closeButton).focus();

    this.toggleOverlay(true);
  }

  // Closes the off-canvas menu
  close() {
    this.isOpen = false;
    this.canvasElement.classList.remove('active');
    this.content.scrollTo({ top: 0, behavior: 'smooth' });

    // Enable page scroll
    document.body.classList.remove('noscroll');

    this.canvasElement.addEventListener(
      'transitionend',
      () => {
        this.resetNestedMenus();
      },
      { once: true }
    );

    this.updateAccessibilityAttributes(false);
    this.toggleOverlay(false);

    this.openButton.focus(); // Return focus to the trigger button
  }

  // Updates the accessibility attributes of the component
  updateAccessibilityAttributes(isOpen) {
    this.canvasElement.setAttribute('aria-hidden', String(!isOpen));
    this.openButton.setAttribute('aria-expanded', String(isOpen));
    this.canvasElement.inert = !isOpen;

    this.nestedContents.forEach((nestedContents) => {
      nestedContents.setAttribute('aria-hidden', 'true');
    });
  }

  // Toggles the visibility and focus of the overlay
  toggleOverlay(isVisible) {
    if (this.overlayElement) {
      this.overlayElement.classList.toggle('active', isVisible);
    }
  }

  // Updates focusable elements within the current active menu
  updateFocusableElements(menu) {
    this.focusableElements = menu.querySelectorAll(
      "a[href]:not([disabled]):not([inert]), button:not([disabled]):not([inert]), input:not([disabled]):not([inert]), textarea:not([disabled]):not([inert]), [tabindex]:not([tabindex='-1']):not([inert])"
    );

    [this.firstFocusableElement, this.lastFocusableElement] = this.focusableElements.length
      ? [this.focusableElements[0], this.focusableElements[this.focusableElements.length - 1]]
      : [null, null];
  }

  // Handles keydown events for focus trapping and menu closing
  handleKeyDown(event) {
    if (!this.isOpen) return;

    if (event.key === 'Tab') {
      this.trapFocus(event);
    } else if (event.key === 'Escape') {
      this.close();
    }
  }

  // Traps focus within the off-canvas menu for accessibility
  trapFocus(event) {
    if (!this.firstFocusableElement || !this.lastFocusableElement) {
      event.preventDefault();
      return;
    }

    if (event.shiftKey && document.activeElement === this.firstFocusableElement) {
      event.preventDefault();
      this.lastFocusableElement.focus();
    } else if (!event.shiftKey && document.activeElement === this.lastFocusableElement) {
      event.preventDefault();
      this.firstFocusableElement.focus();
    }
  }

  // Handles clicks on nested menus to reveal nested content
  handleNestedMenuClick(event, menu) {
    event.preventDefault();
    const menuContent = menu.nextElementSibling;

    if (menuContent) {
      menuContent.classList.add('active');
      menuContent.setAttribute('aria-hidden', 'false');
      menu.setAttribute('aria-expanded', 'true');

      this.toggleFocus(menuContent, true);
      this.updateFocusableElements(menuContent);

      menuContent.addEventListener(
        'transitionend',
        () => {
          menuContent.querySelector('.canvas-nested-back').focus();
        },
        { once: true }
      );

      this.activeMenu = menuContent;
    }
  }

  // Handles clicks on back buttons to hide nested content
  handleBackButtonClick(event) {
    event.preventDefault();
    const menuContent = event.target.closest('.canvas-nested-content');

    menuContent.setAttribute('aria-hidden', 'true');
    menuContent.previousElementSibling.setAttribute('aria-expanded', 'false');

    if (menuContent) {
      menuContent.classList.remove('active');
      menuContent.scrollTo(0, 0);
      menuContent.querySelector('ul').scrollTo(0, 0);
      this.toggleFocus(menuContent, false);

      const nestedMenu = menuContent.previousElementSibling;
      nestedMenu.focus();
      this.activeMenu = this.canvasElement;
      this.updateFocusableElements(this.canvasElement);
    }
  }

  // Resets all nested menus to their initial state
  resetNestedMenus() {
    this.nestedContents.forEach((menuContent) => {
      menuContent.classList.remove('active');
      menuContent.scrollTo(0, 0);
      menuContent.querySelector('ul').scrollTo(0, 0);
      menuContent.parentElement.querySelector('.canvas-nested').setAttribute('aria-expanded', 'false');
      this.toggleFocus(menuContent, false);
    });
  }
}

// Initialization: Instantiates OffCanvas for each relevant element
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-canvas]').forEach((canvas) => new OffCanvas(canvas));
});